import { cloneElement, isValidElement, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Drawer } from '../components/Drawer';
import { MenuModeContext } from '../components/Menu/MenuModeContext';
import type { HeaderProps } from '../components/Header/Header';
import {
  MainContentSurfaceContext,
  type MainContentSurface,
} from './MainContentSurfaceContext';

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  height: 100svh;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
`;

const WorkspaceRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const SidebarWrapper = styled.div`
  flex-shrink: 0;
  align-self: stretch;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  /* Выравниваем первый пункт меню с верхом области прокрутки контента (под шапкой). */
  padding-top: ${({ theme }) => theme.layout.headerHeight}px;

  ${({ theme }) => theme.device.tablet} {
    display: none;
  }
`;

const MainColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

/**
 * Рабочая область маршрутов: по умолчанию белая панель с обводкой (списки, формы).
 * Режим `external` — серая канва без рамки, когда смонтирован {@link ExternalPageCanvas}.
 */
const ContentPanel = styled.main<{ $surface: MainContentSurface }>`
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  background: ${({ theme, $surface }) =>
    $surface === 'external' ? theme.colors.background : theme.colors.white};

  ${({ theme, $surface }) =>
    $surface === 'default' &&
    css`
      ${theme.device.layoutDesktop} {
        border: 1px solid ${theme.colors.divider};
        border-right-width: 0;
        border-bottom-width: 0;
        border-radius: ${theme.layout.mainContentBorderRadius}px 0 0 0;
      }
    `}
`;

export interface MainLayoutProps {
  header: ReactNode;
  menu: ReactNode;
  children: ReactNode;
}

export function MainLayout({ header, menu, children }: MainLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mainContentSurface, setMainContentSurface] = useState<MainContentSurface>('default');
  const mainSurfaceApi = useMemo(() => ({ setSurface: setMainContentSurface }), []);

  const headerWithToggle = isValidElement(header)
    ? cloneElement(header as ReactElement<HeaderProps>, {
        onMenuOpen: () => setDrawerOpen(true),
      })
    : header;

  return (
    <LayoutContainer>
      <WorkspaceRow>
        <SidebarWrapper>
          <MenuModeContext.Provider value="sidebar">
            {menu}
          </MenuModeContext.Provider>
        </SidebarWrapper>
        <MainColumn>
          {headerWithToggle}
          <MainContentSurfaceContext.Provider value={mainSurfaceApi}>
            <ContentPanel $surface={mainContentSurface}>{children}</ContentPanel>
          </MainContentSurfaceContext.Provider>
        </MainColumn>
      </WorkspaceRow>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <MenuModeContext.Provider value="drawer">
          {menu}
        </MenuModeContext.Provider>
      </Drawer>
    </LayoutContainer>
  );
}
